'use client'

import { gql, useQuery } from '@apollo/client'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'

const GET_APPROVED_TESTIMONIALS = gql`
  query GetApprovedTestimonials {
    getApprovedTestimonials {
      id
      xHandle
      company
      content
      rating
      xProfileImage
      xDisplayName
      xVerified
      xVerifiedType
    }
  }
`

export function Testimonials() {
  const { data, loading, error } = useQuery(GET_APPROVED_TESTIMONIALS)
  const [currentPage, setCurrentPage] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (loading) return null
  if (error) return null
  if (!data || data.getApprovedTestimonials.length === 0) return null

  const testimonials = data.getApprovedTestimonials
  const itemsPerPage = 3
  const totalPages = Math.ceil(testimonials.length / itemsPerPage)
  const totalTestimonials = testimonials.length

  const goToNext = () => {
    setExpandedId(null) // Close expanded state
    setCurrentPage((prev) => (prev + 1) % totalPages)
  }

  const goToPrevious = () => {
    setExpandedId(null) // Close expanded state
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages)
  }

  const visibleTestimonials = testimonials.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  )

  const MAX_LENGTH = 150
  const isSingleTestimonial = totalTestimonials === 1

  const toggleExpand = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null)
    } else {
      setExpandedId(id)
    }
  }

  return (
    <section className="py-20 bg-muted/20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-bold tracking-tight mb-3">Testimonials</h2>
          <p className="text-muted-foreground text-base">
            Hear from creators who are using X11.Social
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Testimonials Grid */}
          <div className={`grid gap-6 ${
            expandedId
              ? 'grid-cols-1'
              : isSingleTestimonial
                ? 'grid-cols-1 max-w-2xl mx-auto'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {visibleTestimonials.map((testimonial: any) => {
              const username = testimonial.xHandle?.replace('@', '') || 'User'
              const displayName = testimonial.xDisplayName || testimonial.xHandle || 'Anonymous'
              const profileImage = testimonial.xProfileImage || `https://ui-avatars.com/api/?name=${username}&background=random`
              const xProfileUrl = `https://x.com/${username}`

              const isExpanded = expandedId === testimonial.id
              const isTooLong = testimonial.content.length > MAX_LENGTH
              const displayContent = isExpanded || !isTooLong
                ? testimonial.content
                : testimonial.content.slice(0, MAX_LENGTH) + '...'

              // Hide other cards when one is expanded
              if (expandedId && !isExpanded) return null

              return (
                <Card
                  key={testimonial.id}
                  className={`bg-background border hover:border-primary/50 transition-all ${isExpanded ? 'col-span-full' : ''}`}
                >
                  <CardContent className="p-6">
                    {/* Author Info - Top */}
                    <div className="flex items-center gap-3 mb-4">
                      <Avatar className="h-11 w-11">
                        <AvatarImage src={profileImage} alt={displayName} />
                        <AvatarFallback className="text-sm font-medium">
                          {username.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <a
                            href={xProfileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-sm truncate hover:text-primary transition-colors"
                          >
                            {displayName}
                          </a>
                          {testimonial.xVerified && (
                            <svg
                              className={`w-4 h-4 flex-shrink-0 fill-current ${
                                testimonial.xVerifiedType === 'blue' ? 'text-blue-500' :
                                testimonial.xVerifiedType === 'business' ? 'text-yellow-500' :
                                testimonial.xVerifiedType === 'government' ? 'text-gray-500' :
                                'text-blue-500'
                              }`}
                              viewBox="0 0 24 24"
                              aria-label={`Verified ${testimonial.xVerifiedType || 'account'}`}
                            >
                              <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.26 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.68-.88 3.34-2.19c1.39.45 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.36-6.2 6.77z"/>
                            </svg>
                          )}
                        </div>
                        <a
                          href={xProfileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground truncate hover:text-primary transition-colors inline-block"
                        >
                          {testimonial.xHandle}
                        </a>
                        {testimonial.company && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {testimonial.company}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Rating */}
                    {testimonial.rating && (
                      <div className="flex gap-0.5 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= testimonial.rating
                                ? 'text-yellow-500 fill-yellow-500'
                                : 'text-muted-foreground/20'
                            }`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Content with Quote */}
                    <div className="flex gap-2">
                      <Quote className="w-5 h-5 text-muted-foreground/30 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm leading-relaxed text-foreground/80">
                          {displayContent}
                        </p>
                        {isTooLong && (
                          <button
                            onClick={() => toggleExpand(testimonial.id)}
                            className="text-sm text-primary hover:underline mt-2 font-medium"
                          >
                            {isExpanded ? 'Show less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Navigation - Only show if 3+ testimonials and not expanded */}
          {totalTestimonials >= 3 && !expandedId && (
            <>
              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center justify-between mt-8">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToPrevious}
                  className="h-9 w-9 rounded-full"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>

                <div className="flex gap-2">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentPage
                          ? 'w-8 bg-primary'
                          : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                      }`}
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToNext}
                  className="h-9 w-9 rounded-full"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              {/* Mobile Navigation */}
              <div className="flex lg:hidden items-center justify-center gap-4 mt-8">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToPrevious}
                  className="h-9 w-9 rounded-full"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex gap-2">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentPage
                          ? 'w-8 bg-primary'
                          : 'w-2 bg-muted-foreground/30'
                      }`}
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToNext}
                  className="h-9 w-9 rounded-full"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
